import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Send, Check, Search, Share2 } from 'lucide-react';
import { api } from '../api/client';
import { useTranslation } from '../i18n';
import { nativeShare } from '../utils/share';
import './ShareBibleStudyModal.css';

function slugify(str) {
  return (str || '')
    .toLowerCase()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

// Marker that ChatScreen parses into a tappable Movement Story card.
function buildMarker({ id, title, coverImage }) {
  return `[MOVEMENT_STORY_SHARE]${JSON.stringify({ id, title, coverImage })}[/MOVEMENT_STORY_SHARE]`;
}

/**
 * ShareMovementStoryModal — Movement Stories are always public, so there's no
 * visibility step: copy/native-share the public link, or send it into a DM.
 *
 * Props: story (needs id, title, coverImage), onClose.
 */
export default function ShareMovementStoryModal({ story, onClose }) {
  const { t } = useTranslation('movement');
  const slug = slugify(story.title);
  const url  = `${window.location.origin}/m/${story.id}${slug ? `/${slug}` : ''}`;

  const [picking, setPicking]         = useState(false);
  const [connections, setConnections] = useState(null);
  const [connQuery, setConnQuery]     = useState('');
  const [note, setNote]               = useState('');
  const [busy, setBusy]               = useState(false);
  const [error, setError]             = useState('');
  const [flash, setFlash]             = useState('');
  const [sentTo, setSentTo]           = useState('');

  async function doShare() {
    const r = await nativeShare({ title: story.title || t('shareTitle'), text: t('shareText'), url });
    if (r === 'copied') setFlash(t('linkCopied'));
    else if (r === 'shared') setFlash(t('shared'));
    if (r === 'copied' || r === 'shared') setTimeout(() => setFlash(''), 2000);
  }

  async function openPicker() {
    setPicking(true);
    if (connections === null) {
      try { const d = await api.get('/api/chat/connections'); setConnections(d.connections || []); }
      catch { setConnections([]); }
    }
  }

  async function sendToConnection(person) {
    setBusy(true); setError('');
    try {
      const conv = await api.post('/api/chat/dm', { otherUserId: person.id });
      const convId = conv.conversation?.id || conv.id;
      const marker = buildMarker({ id: story.id, title: story.title, coverImage: story.coverImage });
      const content = note.trim() ? `${note.trim()}\n${marker}` : marker;
      await api.post(`/api/chat/dm/${convId}/messages`, { content });
      setSentTo(person.name || person.firstName || '');
      setTimeout(onClose, 1400);
    } catch (err) {
      setError(err.message || t('submitFailed'));
    } finally { setBusy(false); }
  }

  const filteredConns = (connections || []).filter(c =>
    !connQuery || (c.name || '').toLowerCase().includes(connQuery.toLowerCase()));

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal sbs-modal">
        <div className="modal-header">
          <span className="modal-title">{t('shareTitle')}</span>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        {error && <div className="form-error">{error}</div>}

        {sentTo ? (
          <div className="sbs-success"><Check size={18} /> {t('shareSentTo', sentTo)}</div>
        ) : picking ? (
          <>
            <p className="sbs-sub">{t('shareSendSub')}</p>
            <div className="sbs-search">
              <Search size={14} />
              <input placeholder={t('shareSearchConnections')} value={connQuery}
                onChange={e => setConnQuery(e.target.value)} />
            </div>
            <div className="sbs-conn-list">
              {connections === null ? (
                <div className="sbs-loading"><span className="spinner" /></div>
              ) : filteredConns.length === 0 ? (
                <p className="sbs-empty">{t('shareNoConnections')}</p>
              ) : filteredConns.map(c => (
                <button key={c.id} className="sbs-conn-row" disabled={busy} onClick={() => sendToConnection(c)}>
                  <span className="sbs-conn-avatar">
                    {c.avatar ? <img src={c.avatar} alt="" /> : (c.name || '?')[0]?.toUpperCase()}
                  </span>
                  <span className="sbs-conn-name">{c.name}</span>
                  <Send size={14} />
                </button>
              ))}
            </div>
            <textarea className="create-textarea" rows={2} placeholder={t('shareDmNotePlaceholder')}
              value={note} onChange={e => setNote(e.target.value)} />
            <div className="sbs-actions">
              <button className="btn btn-outline" onClick={() => setPicking(false)}>{t('back')}</button>
            </div>
          </>
        ) : (
          <>
            <div className="sbs-link-row">
              <input readOnly value={url} onFocus={e => e.target.select()} />
              <button className="btn btn-primary sbs-copy" onClick={doShare}>
                {flash ? <><Check size={14} /> {flash}</> : <><Share2 size={14} /> {t('shareCopyOrShare')}</>}
              </button>
            </div>
            <div className="sbs-actions">
              <button className="btn btn-outline" onClick={onClose}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={openPicker}>
                <Send size={14} /> {t('sendInMessage')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
